import React from "react";
import Tag from "../../../../ui-components/molecules/tag/tag";
import Loading from "../../../../ui-components/loading/loading";
import { getStatusColor } from "../../../../services/pickups";
import {
  DuplicatePickupCriteria,
  useDuplicatePickupCheck,
} from "../utils/duplicatePickupUtils";

export interface DuplicatePickupCheckProps extends Partial<DuplicatePickupCriteria> {
  excludePickupNumber?: string;
  onPickupNumberClick?: (pickupNumber: string) => void;
}

const formatStatusLabel = (status: string): string => {
  if (!status) return "-";
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const DuplicatePickupCheck: React.FC<DuplicatePickupCheckProps> = ({
  bolNumber,
  teamId,
  shipperZipCode,
  consigneeZipCode,
  excludePickupNumber,
  onPickupNumberClick,
}) => {
  const hasBolAndZips = Boolean(
    bolNumber?.trim() && shipperZipCode?.trim() && consigneeZipCode?.trim()
  );

  if (!hasBolAndZips) {
    return null;
  }

  if (!teamId?.trim()) {
    return (
      <div className="xgs-pickup-inbox__duplicate-check">
        <p className="xgs-pickup-inbox__duplicate-check__message">
          Select requester company to check for duplicates.
        </p>
      </div>
    );
  }

  return (
    <DuplicatePickupCheckResults
      bolNumber={bolNumber!}
      teamId={teamId}
      shipperZipCode={shipperZipCode!}
      consigneeZipCode={consigneeZipCode!}
      excludePickupNumber={excludePickupNumber}
      onPickupNumberClick={onPickupNumberClick}
    />
  );
};

interface DuplicatePickupCheckResultsProps extends DuplicatePickupCriteria {
  excludePickupNumber?: string;
  onPickupNumberClick?: (pickupNumber: string) => void;
}

const DuplicatePickupCheckResults: React.FC<DuplicatePickupCheckResultsProps> = ({
  bolNumber,
  teamId,
  shipperZipCode,
  consigneeZipCode,
  excludePickupNumber,
  onPickupNumberClick,
}) => {
  const { data: duplicates, isLoading, isFetching, isError } = useDuplicatePickupCheck(
    { bolNumber, teamId, shipperZipCode, consigneeZipCode },
    excludePickupNumber
  );

  const showLoading = isLoading || isFetching;

  if (showLoading) {
    return (
      <div className="xgs-pickup-inbox__duplicate-check">
        <Loading isLoading mix="xgs-pickup-inbox__duplicate-check__loading">
          <span className="xgs-pickup-inbox__duplicate-check__message">
            Checking for duplicates...
          </span>
        </Loading>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="xgs-pickup-inbox__duplicate-check xgs-pickup-inbox__duplicate-check--error">
        <p className="xgs-pickup-inbox__duplicate-check__message">
          Unable to check for duplicate pickups.
        </p>
      </div>
    );
  }

  const matches = duplicates ?? [];

  if (matches.length === 0) {
    return (
      <div className="xgs-pickup-inbox__duplicate-check xgs-pickup-inbox__duplicate-check--info">
        <p className="xgs-pickup-inbox__duplicate-check__message">
          No potential duplicate pickups found.
        </p>
      </div>
    );
  }

  return (
    <div className="xgs-pickup-inbox__duplicate-check xgs-pickup-inbox__duplicate-check--warning">
      <p className="xgs-pickup-inbox__duplicate-check__message">
        {matches.length} potential duplicate{matches.length === 1 ? "" : "s"} found.
      </p>
      <ul className="xgs-pickup-inbox__duplicate-check__list">
        {matches.map((duplicate) => (
          <li key={duplicate.pickupNumber} className="xgs-pickup-inbox__duplicate-check__item">
            <span
              role="link"
              tabIndex={0}
              className="xgs-pickup-inbox__duplicate-check__pickup-link"
              onClick={() => onPickupNumberClick?.(duplicate.pickupNumber)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onPickupNumberClick?.(duplicate.pickupNumber);
                }
              }}
            >
              {duplicate.pickupNumber}
            </span>
            <Tag mods={{ color: getStatusColor(duplicate.status) }}>
              {formatStatusLabel(duplicate.status)}
            </Tag>
            <span className="xgs-pickup-inbox__duplicate-check__created">
              {duplicate.createdAt
                ? duplicate.createdAt.toUiDateTimeShortFormat()
                : "-"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DuplicatePickupCheck;
